import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Container,
  Grid,
  IconButton,
  Snackbar,
  Paper,
  Divider,
  Chip,
  CircularProgress,
  Avatar,
  Alert,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  ArrowBack,
  Person,
  Email,
  Phone,
  CalendarToday,
  HomeOutlined,
  QuestionAnswer,
  Upload as UploadIcon,
  PictureAsPdf as PdfIcon,
  MedicalServices,
  Business,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { bookingsService } from './bookings.service';
import {
  BOOKING_STATUS,
  PAYMENT_STATUS,
  DEFAULT_CURRENCY,
  REPORT_STATUS,
  PAYMENT_METHOD,
} from '@/constants';
import { formatDate, formatDateTime, formatTime } from '../../helpers';

const ViewBooking = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { token, logout } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [booking, setBooking] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info',
  });
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  // const [pdfPassword, setPdfPassword] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [emailCustomer, setEmailCustomer] = useState(true);

  // Set up logout callback and fetch data
  useEffect(() => {
    bookingsService.setLogoutCallback(logout);

    const fetchBooking = async () => {
      if (!token || !id) return;

      try {
        setLoading(true);
        const data = await bookingsService.getBooking(token, parseInt(id));
        console.log('Fetched booking data:', data);
        setBooking(data);
      } catch (err) {
        console.error('Error fetching booking:', err);
        setSnackbar({
          open: true,
          message:
            err instanceof Error ? err.message : 'Failed to fetch booking data',
          severity: 'error',
        });
        navigate('/bookings');
      } finally {
        setLoading(false);
      }
    };

    fetchBooking();
  }, [token, id, navigate, logout]);

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleUploadClick = () => {
    setUploadDialogOpen(true);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.includes('pdf')) {
        setSnackbar({
          open: true,
          message: 'Please select a PDF file',
          severity: 'error',
        });
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUploadConfirm = async () => {
    if (!token || !id || !selectedFile) return;

    try {
      setUploading(true);
      setUploadProgress(0);

      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('emailCustomer', String(emailCustomer));
      // formData.append('password', pdfPassword);

      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 200);

      await bookingsService.uploadReport(token, parseInt(id), formData);

      clearInterval(progressInterval);
      setUploadProgress(100);

      setSnackbar({
        open: true,
        message: 'Report uploaded successfully',
        severity: 'success',
      });
      setUploadDialogOpen(false);
      setSelectedFile(null);
      // setPdfPassword('');
      setUploadProgress(0);

      // Refresh booking data
      const data = await bookingsService.getBooking(token, parseInt(id));
      setBooking(data);
    } catch (err) {
      console.error('Error uploading report:', err);
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : 'Failed to upload report',
        severity: 'error',
      });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleUploadCancel = () => {
    setUploadDialogOpen(false);
    setSelectedFile(null);
    setEmailCustomer(true);
    // setPdfPassword('');
    setUploadProgress(0);
  };

  const handleViewPdf = async () => {
    if (!token || !id) return;

    try {
      const blob = await bookingsService.downloadReport(token, parseInt(id));

      // Create a URL for the blob
      const url = URL.createObjectURL(blob);

      // Open PDF in new tab
      window.open(url, '_blank');

      // Clean up the URL after a delay to free memory
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 1000);

      setSnackbar({
        open: true,
        message: 'PDF report opened successfully',
        severity: 'success',
      });
    } catch (err) {
      console.error('Error viewing PDF:', err);
      setSnackbar({
        open: true,
        message:
          err instanceof Error ? err.message : 'Failed to view PDF report',
        severity: 'error',
      });
    }
  };

  const formatAnswer = (answer: string | null | undefined) => {
    // Handle null, undefined, or empty values
    if (!answer) {
      return 'N/A';
    }

    // Convert camelCase or snake_case to readable format
    return answer
      .replace(/([A-Z])/g, ' $1')
      .replace(/_/g, ' ')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading booking details...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (!booking) {
    return null;
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <Box display="flex" alignItems="center">
              <IconButton onClick={() => navigate('/bookings')} sx={{ mr: 2 }}>
                <ArrowBack />
              </IconButton>
              <Typography variant="h4" component="h1">
                Booking Details
              </Typography>
            </Box>
            <Box display="flex" gap={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                {booking.bookingRef}
              </Typography>
              <Chip
                label={BOOKING_STATUS(booking.status).label}
                color={BOOKING_STATUS(booking.status).color as any}
                variant="outlined"
              />
            </Box>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Complete booking information and patient details
          </Typography>
        </Paper>

        <Grid container spacing={3}>
          {/* Patient Avatar Section */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    py: 3,
                  }}
                >
                  <Avatar
                    sx={{
                      width: 120,
                      height: 120,
                      bgcolor: 'primary.main',
                      fontSize: 48,
                      mb: 2,
                    }}
                  >
                    <Person fontSize="large" />
                  </Avatar>
                  <Typography variant="h5" gutterBottom>
                    {booking.firstName} {booking.lastName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Patient
                  </Typography>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ mt: 2 }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Email fontSize="small" color="action" />
                    <Typography variant="body2">{booking.email}</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Phone fontSize="small" color="action" />
                    <Typography variant="body2">{booking.phone}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Booking Information
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Booking Reference
                  </Typography>
                  <Typography variant="body1" fontWeight="bold" gutterBottom>
                    {booking.bookingRef}
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 2 }}
                  >
                    Booking Status
                  </Typography>
                  <Chip
                    label={BOOKING_STATUS(booking.status).label}
                    color={BOOKING_STATUS(booking.status).color as any}
                    size="small"
                    sx={{ mt: 0.5, mb: 2 }}
                  />

                  <Typography variant="body2" color="text.secondary">
                    Report Status
                  </Typography>
                  <Chip
                    label={REPORT_STATUS(booking.reportStatus).label}
                    color={REPORT_STATUS(booking.reportStatus).color as any}
                    size="small"
                    sx={{ mt: 0.5, mb: 2 }}
                  />

                  <Typography variant="body2" color="text.secondary">
                    Created On
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {formatDate(booking.createdAt)}
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 2 }}
                  >
                    Last Updated
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(booking.updatedAt)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Details Section */}
          <Grid item xs={12} md={8}>
            {/* Patient Information */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Patient Information
                </Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      First Name
                    </Typography>
                    <Typography variant="body1">{booking.firstName}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Last Name
                    </Typography>
                    <Typography variant="body1">{booking.lastName}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Date of Birth
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(booking.dob)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Gender
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ textTransform: 'capitalize' }}
                    >
                      {booking.gender}
                    </Typography>
                  </Grid>
                  {booking.healthCard && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Health Card
                      </Typography>
                      <Typography variant="body1">
                        {booking.healthCard}
                      </Typography>
                    </Grid>
                  )}
                  {booking.versionCode && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Version Code
                      </Typography>
                      <Typography variant="body1">
                        {booking.versionCode}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>

            {/* Service Information */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <MedicalServices color="action" />
                  <Typography variant="h6">Service Information</Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Service Title
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {booking.title}
                    </Typography>
                  </Grid>
                  {booking.description && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        Description
                      </Typography>
                      <Paper
                        variant="outlined"
                        sx={{ p: 2, mt: 1, bgcolor: 'grey.50' }}
                      >
                        <Typography variant="body2">
                          {booking.description}
                        </Typography>
                      </Paper>
                    </Grid>
                  )}

                  {booking.duration && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Duration
                      </Typography>
                      <Typography variant="body1">
                        {booking.duration}min
                      </Typography>
                    </Grid>
                  )}
                  {booking.promoCode && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Sub Total Amount
                        </Typography>
                        <Typography variant="h6" color="primary">
                          {DEFAULT_CURRENCY}
                          {booking.subtotalAmount}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Promo Code
                        </Typography>
                        <Typography variant="h6" color="primary">
                          {booking.promoCode.toUpperCase()}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Discount Type
                        </Typography>
                        <Typography variant="h6" color="primary">
                          {booking.discountType.toUpperCase()}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Discount Value
                        </Typography>
                        <Typography variant="h6" color="primary">
                          {booking.discountType === 'percentage'
                            ? `${Math.round(booking.discountValue)}%`
                            : `${DEFAULT_CURRENCY}${booking.discountValue}`}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Discount Amount
                        </Typography>
                        <Typography variant="h6" color="primary">
                          {DEFAULT_CURRENCY}
                          {booking.discountAmount || 0}
                        </Typography>
                      </Grid>
                    </>
                  )}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Total Amount
                    </Typography>
                    <Typography variant="h6" color="primary">
                      {DEFAULT_CURRENCY}
                      {booking.amount}
                    </Typography>
                  </Grid>

                  {booking.medicalInformation && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        Medical Information
                      </Typography>
                      <Paper
                        variant="outlined"
                        sx={{ p: 2, mt: 1, bgcolor: 'grey.50' }}
                      >
                        <Typography variant="body2">
                          {booking.medicalInformation}
                        </Typography>
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>

            {/* Diagnostic Center Information */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <Business color="action" />
                  <Typography variant="h6">Diagnostic Center</Typography>
                </Box>
                <Grid container spacing={2}>
                  {booking.diagnosticCenterAddress && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        Address
                      </Typography>
                      <Typography variant="body1">
                        {booking.diagnosticCenterAddress}
                      </Typography>
                    </Grid>
                  )}
                  {booking.diagnosticCenterCity && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        City
                      </Typography>
                      <Typography variant="body1">
                        {booking.diagnosticCenterCity}
                      </Typography>
                    </Grid>
                  )}
                  {booking.diagnosticCenterPostalCode && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Postal Code
                      </Typography>
                      <Typography variant="body1">
                        {booking.diagnosticCenterPostalCode}
                      </Typography>
                    </Grid>
                  )}
                  {booking.diagnosticCenterEmail && (
                    <Grid item xs={12} sm={6}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Email fontSize="small" color="action" />
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Email
                          </Typography>
                          <Typography variant="body1">
                            {booking.diagnosticCenterEmail}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}
                  {booking.diagnosticCenterPhone && (
                    <Grid item xs={12} sm={6}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Phone fontSize="small" color="action" />
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Phone
                          </Typography>
                          <Typography variant="body1">
                            {booking.diagnosticCenterPhone}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>

            {/* Appointment Details */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Appointment Details
                </Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <CalendarToday fontSize="small" color="action" />
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Appointment Date
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                          {formatDate(booking.bookingDate)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <CalendarToday fontSize="small" color="action" />
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Appointment Time
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                          {formatTime(booking.bookingTime)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Address Information */}
            {(booking.streetAddress || booking.city || booking.postalCode) && (
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <HomeOutlined color="action" />
                    <Typography variant="h6">Address Information</Typography>
                  </Box>
                  <Grid container spacing={2}>
                    {booking.streetAddress && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Street Address
                        </Typography>
                        <Typography variant="body1">
                          {booking.streetAddress}
                        </Typography>
                      </Grid>
                    )}
                    {booking.city && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          City
                        </Typography>
                        <Typography variant="body1">{booking.city}</Typography>
                      </Grid>
                    )}
                    {booking.province && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Province
                        </Typography>
                        <Typography variant="body1">
                          {booking.province}
                        </Typography>
                      </Grid>
                    )}
                    {booking.postalCode && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Postal Code
                        </Typography>
                        <Typography variant="body1">
                          {booking.postalCode}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            )}

            {/* Medical Questionnaires */}
            {booking.questionnaires && booking.questionnaires.length > 0 && (
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <QuestionAnswer color="action" />
                    <Typography variant="h6">Medical Questionnaire</Typography>
                  </Box>
                  <Grid container spacing={2}>
                    {booking.questionnaires.map((q: any, index: number) => (
                      <Grid item xs={12} key={q.id}>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 2,
                            bgcolor:
                              index % 2 === 0 ? 'grey.50' : 'background.paper',
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            fontWeight="medium"
                            gutterBottom
                          >
                            {index + 1}. {q.question}
                          </Typography>
                          <Typography variant="body1" fontWeight="bold">
                            {formatAnswer(q.answer)}
                          </Typography>
                          {q.followUpAnswer && (
                            <Typography
                              variant="body2"
                              fontWeight="bold"
                              sx={{ mt: 1 }}
                            >
                              Follow-up: {formatAnswer(q.followUpQuestion)} (
                              {formatAnswer(q.followUpAnswer)})
                            </Typography>
                          )}
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            )}

            {/* Payment Information */}
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Payment Information
                </Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Payment Status
                    </Typography>
                    <Chip
                      label={PAYMENT_STATUS(booking.paymentStatus).label}
                      color={PAYMENT_STATUS(booking.paymentStatus).color as any}
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Payment Method
                    </Typography>
                    <Typography variant="body1">
                      {PAYMENT_METHOD(booking.paymentMethod).label}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Payment Intent ID
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                        wordBreak: 'break-all',
                      }}
                    >
                      {booking.paymentIntentId || 'N/A'}
                    </Typography>
                  </Grid>
                  {/* <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Payment Reference
                    </Typography>
                    <Typography variant="body1">
                      {booking.paymentReference || 'N/A'}
                    </Typography>
                  </Grid> */}
                </Grid>
              </CardContent>
            </Card>

            {/* Report Section */}
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  mb={2}
                >
                  <Typography variant="h6">Report</Typography>
                  <Box display="flex" gap={1}>
                    {booking.paymentStatus === 1 &&
                      booking.reportStatus !== 1 && (
                        <Button
                          variant="outlined"
                          startIcon={<UploadIcon />}
                          onClick={handleUploadClick}
                          disabled={loading}
                        >
                          Upload Report
                        </Button>
                      )}
                    {booking.reportStatus === 1 && (
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<PdfIcon />}
                        onClick={handleViewPdf}
                        disabled={loading}
                      >
                        View PDF Report
                      </Button>
                    )}
                  </Box>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Report Status
                    </Typography>
                    <Chip
                      label={REPORT_STATUS(booking.reportStatus).label}
                      color={REPORT_STATUS(booking.reportStatus).color as any}
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Upload Report Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={handleUploadCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Upload Report</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={3} sx={{ mt: 2 }}>
            {/* File Selection */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Select PDF File
              </Typography>
              <input
                accept=".pdf"
                style={{ display: 'none' }}
                id="file-upload"
                type="file"
                onChange={handleFileSelect}
                disabled={uploading}
              />
              <label htmlFor="file-upload">
                <Button
                  variant="outlined"
                  component="span"
                  disabled={uploading}
                  startIcon={<UploadIcon />}
                >
                  Choose PDF File
                </Button>
              </label>
              {selectedFile && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Selected: {selectedFile.name}
                </Typography>
              )}
              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={emailCustomer}
                      onChange={e => setEmailCustomer(e.target.checked)}
                      disabled={uploading}
                      color="primary"
                    />
                  }
                  label="Email medical report to customer"
                />
              </Box>
            </Box>
            {/* Upload Progress */}
            {uploading && (
              <Box>
                <Typography variant="body2" gutterBottom>
                  Uploading... {uploadProgress}%
                </Typography>
                <LinearProgress variant="determinate" value={uploadProgress} />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUploadCancel} disabled={uploading}>
            Cancel
          </Button>
          <Button
            onClick={handleUploadConfirm}
            variant="contained"
            disabled={!selectedFile || uploading}
            startIcon={
              uploading ? <CircularProgress size={20} /> : <UploadIcon />
            }
          >
            {uploading ? 'Uploading...' : 'Upload Report'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ViewBooking;
